/**
 * WebSocket Service for FinCreator Real-Time Alerts
 *
 * This is a shared singleton - multiple hooks/components may use the same
 * connection at once. Use acquire()/release() (not connect()/disconnect())
 * from consumers so the socket stays open as long as anyone needs it.
 *
 * Usage:
 * ```typescript
 * import { websocketService } from './services/websocket';
 *
 * // Acquire a shared connection
 * websocketService.acquire(token, ['signals', 'market']);
 *
 * // Listen for events
 * websocketService.on('signal_alert', (data) => {
 *   console.log('New signal:', data);
 * });
 *
 * // Subscribe to specific symbol
 * websocketService.subscribe(['symbol:BTC/USDT']);
 *
 * // Release when this consumer no longer needs the connection
 * websocketService.release();
 * ```
 */

type MessageHandler = (data: any) => void;
type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface WebSocketMessage {
  type: string;
  timestamp?: string;
  [key: string]: any;
}

interface SignalAlertData {
  id: number;
  symbol: string;
  signal_type: string;
  confidence_score: number;
  entry_price: number;
  take_profit_price?: number;
  stop_loss_price?: number;
  timeframe?: string;
}

interface PriceAlertData {
  symbol: string;
  current_price: number;
  trigger_price: number;
  condition: string;
}

interface MarketUpdateData {
  alert_type: string;
  symbol?: string;
  [key: string]: any;
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private token: string | null = null;
  private baseUrl: string;
  private subscriptions: string[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private listeners: Map<string, MessageHandler[]> = new Map();
  private connectionStatus: ConnectionStatus = 'disconnected';
  private refCount = 0;

  constructor(baseUrl: string = 'ws://localhost:8000') {
    this.baseUrl = baseUrl;
  }

  /**
   * Connect to WebSocket server
   */
  connect(token: string, subscriptions: string[] = ['signals', 'market']): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.warn('WebSocket already connected');
      return;
    }

    this.token = token;
    this.subscriptions = subscriptions;
    this.connectionStatus = 'connecting';

    const url = `${this.baseUrl}/api/ws/alerts`;

    try {
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log('✅ WebSocket connected');
        this.connectionStatus = 'connected';
        this.reconnectAttempts = 0;
        // The server authenticates via the first message rather than a URL
        // query param, so the JWT never appears in server access logs.
        this.sendMessage({
          type: 'auth',
          token: this.token,
          subscriptions: this.subscriptions,
        });
        this.startHeartbeat();
        this.emit('connection', { status: 'connected' });
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        this.connectionStatus = 'error';
        this.emit('error', { error });
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.connectionStatus = 'disconnected';
        this.stopHeartbeat();
        this.emit('connection', { status: 'disconnected' });
        this.attemptReconnect();
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this.connectionStatus = 'error';
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.refCount = 0;
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnection
    this.stopHeartbeat();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.connectionStatus = 'disconnected';
  }

  /**
   * Acquire a shared connection on behalf of a consumer (e.g. a React
   * hook). Multiple consumers can call this concurrently - if a connection
   * is already open, this consumer's topics are merged into it instead of
   * opening a second socket. Pair every acquire() with a release().
   */
  acquire(token: string, subscriptions: string[] = ['signals', 'market']): void {
    this.refCount++;

    if (this.isConnected()) {
      const newTopics = subscriptions.filter((topic) => !this.subscriptions.includes(topic));
      if (newTopics.length > 0) {
        this.subscribe(newTopics);
      }
      return;
    }

    this.connect(token, subscriptions);
  }

  /**
   * Release a connection acquired via acquire(). The underlying socket is
   * only closed once every consumer has released it, so one component
   * unmounting doesn't drop the connection for the others still using it.
   */
  release(): void {
    if (this.refCount === 0) {
      return;
    }

    this.refCount--;

    if (this.refCount === 0) {
      this.disconnect();
    }
  }

  /**
   * Subscribe to additional topics
   */
  subscribe(topics: string[]): void {
    this.sendMessage({
      type: 'subscribe',
      topics
    });
    this.subscriptions.push(...topics);
  }

  /**
   * Unsubscribe from topics
   */
  unsubscribe(topics: string[]): void {
    this.sendMessage({
      type: 'unsubscribe',
      topics
    });
    this.subscriptions = this.subscriptions.filter(t => !topics.includes(t));
  }

  /**
   * Send an arbitrary message frame to the server, e.g. a Live Trading Room
   * chat_message frame. Public wrapper around the private sendMessage() so
   * consumers don't need to reimplement the open-socket check.
   */
  send(message: any): void {
    this.sendMessage(message);
  }

  /**
   * Register event listener
   */
  on(event: string, handler: MessageHandler): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(handler);
  }

  /**
   * Remove event listener
   */
  off(event: string, handler: MessageHandler): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index !== -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Get connection status
   */
  getStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Send message to server
   */
  private sendMessage(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, cannot send message');
    }
  }

  /**
   * Handle incoming message
   */
  private handleMessage(message: WebSocketMessage): void {
    const { type } = message;

    // Handle special message types
    switch (type) {
      case 'signal_alert':
        this.handleSignalAlert(message);
        break;
      case 'price_alert':
        this.handlePriceAlert(message);
        break;
      case 'market_update':
        this.handleMarketUpdate(message);
        break;
      case 'analytics_update':
        this.handleAnalyticsUpdate(message);
        break;
      case 'heartbeat':
        // Silent handling
        break;
      case 'pong':
        // Silent handling
        break;
      case 'error':
        console.error('Server error:', message.message);
        break;
    }

    // Emit the raw message type, except for types whose handler above
    // already emits a same-named event with the appropriate payload shape -
    // emitting both would deliver duplicate events to listeners.
    if (type !== 'price_alert' && type !== 'market_update') {
      this.emit(type, message);
    }

    // Emit generic 'message' event
    this.emit('message', message);
  }

  /**
   * Handle signal alert
   */
  private handleSignalAlert(message: any): void {
    const { alert_type, data } = message;

    if (alert_type === 'new_signal') {
      const signal: SignalAlertData = data;
      console.log(`🚨 New ${signal.signal_type} signal for ${signal.symbol}`);
      this.emit('new_signal', signal);
    } else if (alert_type === 'signal_update') {
      console.log(`📊 Signal update: ${message.update_type}`);
      this.emit('signal_update', message);
    }
  }

  /**
   * Handle price alert
   */
  private handlePriceAlert(message: any): void {
    const alert: PriceAlertData = message;
    console.log(`💰 Price alert: ${alert.symbol} ${alert.condition} ${alert.trigger_price}`);
    this.emit('price_alert', alert);
  }

  /**
   * Handle market update
   */
  private handleMarketUpdate(message: any): void {
    const data: MarketUpdateData = message.data;
    console.log(`📈 Market update: ${data.alert_type}`);
    this.emit('market_update', data);
  }

  /**
   * Handle analytics update
   */
  private handleAnalyticsUpdate(message: any): void {
    const { subtype, data } = message;
    if (subtype === 'snapshot') {
      this.emit('analytics_snapshot', data);
    } else if (subtype === 'publication_engagement') {
      this.emit('publication_engagement', data);
    }
  }

  /**
   * Emit event to listeners
   */
  private emit(event: string, data: any): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in ${event} handler:`, error);
        }
      });
    }
  }

  /**
   * Start heartbeat
   */
  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      this.sendMessage({ type: 'ping' });
    }, 30000); // Every 30 seconds
  }

  /**
   * Stop heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Attempt to reconnect
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnection attempts reached');
      this.emit('reconnect_failed', {});
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(30000, 1000 * Math.pow(2, this.reconnectAttempts - 1));
    console.log(`Reconnecting in ${delay}ms (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

    setTimeout(() => {
      if (this.token) {
        this.connect(this.token, this.subscriptions);
      }
    }, delay);
  }
}

// Export singleton instance
export const websocketService = new WebSocketService(
  process.env.REACT_APP_WS_URL || 'ws://localhost:8000'
);

export default websocketService;
